(function () {
  const App = window.WallArtApp = window.WallArtApp || {};

  App.state = {
    hasFurniture: true,
    hasHeadboard: true,
    region: "metric",
    selectedSizeOption: "recommended",
    lastResult: null,
    engine: null
  };

  App.constants = {
    inputLimits: {
      wallWidth: { min: 80, max: 2000, previewMax: 1000 },
      ceilingHeight: { min: 180, max: 600, previewMax: 400 },
      furnitureWidth: { min: 40, max: 1200, previewMax: 600 },
      furnitureHeight: { min: 20, max: 350, previewMax: 220 },
      furnitureCeilingClearanceMin: 20,
      furnitureCeilingClearancePreviewMin: 40
    },
    roomSourceNames: {
      living: "Living room",
      bedroom: "Bedroom",
      hallway: "Hallway",
      dining: "Dining room",
      bathroom: "Bathroom",
      kitchen: "Kitchen"
    },
    roomFlagsByType: {
      living: [],
      bedroom: [],
      dining: ["seated_primary"],
      hallway: ["circulation_route"],
      bathroom: ["wet_zone"],
      kitchen: []
    },
    roomDefaults: {
      living: { hasFurniture: true, furnitureType: "sofa3" },
      bedroom: { hasFurniture: true, furnitureType: "bedKing" },
      dining: { hasFurniture: true, furnitureType: "diningTable" },
      hallway: { hasFurniture: true, furnitureType: "console" },
      bathroom: { hasFurniture: true, furnitureType: "vanity" },
      kitchen: { hasFurniture: true, furnitureType: "worktop" }
    },
    furnitureDb: {
      armchair: { w: 90, h: 85, label: "armchair", optionLabel: "Armchair" },
      sofa2: { w: 160, h: 85, label: "2-seater sofa", optionLabel: "2-seater sofa" },
      sofa3: { w: 210, h: 85, label: "3-seater sofa", optionLabel: "3-seater sofa" },
      customSofa: { w: 210, h: 85, label: "custom sofa", optionLabel: "Custom sofa size" },
      bedSingle: { w: 90, h: 110, noHeadboardH: 60, label: "single bed", optionLabel: "Single bed" },
      bedDouble: { w: 135, h: 110, noHeadboardH: 60, label: "double bed", optionLabel: "Double bed" },
      bedKing: { w: 150, h: 115, noHeadboardH: 60, label: "king bed", optionLabel: "King bed" },
      bedSuperKing: { w: 180, h: 115, noHeadboardH: 60, label: "super king bed", optionLabel: "Super king bed" },
      customBed: { w: 150, h: 115, noHeadboardH: 60, label: "custom bed", optionLabel: "Custom bed size" },
      diningTable: { w: 140, h: 74, label: "dining table", optionLabel: "Dining table" },
      sideboard: { w: 160, h: 80, label: "sideboard / cabinet", optionLabel: "Sideboard / cabinet" },
      console: { w: 120, h: 80, label: "console table", optionLabel: "Console table" },
      vanity: { w: 90, h: 85, label: "vanity unit", optionLabel: "Vanity unit" },
      bath: { w: 170, h: 55, label: "bath", optionLabel: "Bath" },
      worktop: { w: null, h: 90, label: "worktop / unit", optionLabel: "Worktop / unit", usePresetWidth: true }
    },
    roomFurnitureOptions: {
      living: ["armchair", "sofa2", "sofa3", "customSofa", "sideboard"],
      bedroom: ["bedSingle", "bedDouble", "bedKing", "bedSuperKing", "customBed", "sideboard"],
      dining: ["diningTable", "sideboard"],
      hallway: ["console", "sideboard"],
      bathroom: ["vanity", "bath", "sideboard"],
      kitchen: ["sideboard", "worktop"]
    },
    uiToEngineFurnitureType: {
      armchair: "armchair",
      sofa2: "sofa_2_seater",
      sofa3: "sofa_3_seater",
      customSofa: "sofa_custom",
      bedSingle: "bed_single",
      bedDouble: "bed_double",
      bedKing: "bed_king",
      bedSuperKing: "bed_super_king",
      customBed: "bed_custom",
      sideboard: "sideboard",
      console: "console_table",
      vanity: "vanity_unit",
      bath: "bath",
      worktop: "kitchen_worktop",
      diningTable: "dining_table"
    },
    furnitureTypesWithEditableDimensions: [
      "customSofa",
      "customBed",
      "diningTable",
      "sideboard",
      "console",
      "vanity",
      "bath",
      "worktop"
    ],
    layoutPreferenceMap: {
      auto: "auto",
      single: "single",
      pair: "pair",
      triptych: "triptych"
    },
    orientationPreferenceMap: {
      auto: "auto",
      portrait: "portrait",
      landscape: "landscape"
    },
    engineFurnitureDefaults: {
      armchair: { anchorType: "sofa", defaultWidthCm: 90, defaultTopHeightCm: 85 },
      sofa_2_seater: { anchorType: "sofa", defaultWidthCm: 160, defaultTopHeightCm: 85 },
      sofa_3_seater: { anchorType: "sofa", defaultWidthCm: 210, defaultTopHeightCm: 85 },
      sofa_custom: { anchorType: "sofa", defaultWidthCm: 210, defaultTopHeightCm: 85 },
      sofa_large: { anchorType: "sofa", defaultWidthCm: 275, defaultTopHeightCm: 85 },
      bed_single: { anchorType: "bed", defaultWidthCm: 90, defaultTopHeightCm: 110 },
      bed_small_double: { anchorType: "bed", defaultWidthCm: 120, defaultTopHeightCm: 110 },
      bed_double: { anchorType: "bed", defaultWidthCm: 135, defaultTopHeightCm: 110 },
      bed_king: { anchorType: "bed", defaultWidthCm: 150, defaultTopHeightCm: 115 },
      bed_super_king: { anchorType: "bed", defaultWidthCm: 180, defaultTopHeightCm: 115 },
      bed_custom: { anchorType: "bed", defaultWidthCm: 150, defaultTopHeightCm: 115 },
      sideboard: { anchorType: "sideboard", defaultWidthCm: 160, defaultTopHeightCm: 80 },
      console_table: { anchorType: "console", defaultWidthCm: 120, defaultTopHeightCm: 80 },
      dining_table: { anchorType: "desk", defaultWidthCm: 140, defaultTopHeightCm: 74 },
      desk: { anchorType: "desk", defaultWidthCm: 140, defaultTopHeightCm: 74 },
      vanity_unit: { anchorType: "vanity", defaultWidthCm: 90, defaultTopHeightCm: 85 },
      bath: { anchorType: "bath", defaultWidthCm: 170, defaultTopHeightCm: 55 },
      kitchen_worktop: { anchorType: "sideboard", defaultWidthCm: null, defaultTopHeightCm: 90 },
      none: { anchorType: "none", defaultWidthCm: null, defaultTopHeightCm: null }
    }
  };

  App.data = {
    roomSizes: [],
    roomPresets: {},
    engineRoomPresets: {}
  };

  App.formatValue = function (value) {
    return App.state.region === "imperial"
      ? +(value / 2.54).toFixed(1)
      : Math.round(value);
  };

  App.unitLabel = function () {
    return App.state.region === "imperial" ? "in" : "cm";
  };

  App.setPressedState = function (button, isPressed) {
    if (button) {
      button.setAttribute("aria-pressed", isPressed ? "true" : "false");
    }
  };

  App.getValidationField = function (fieldKey) {
    const fieldMap = {
      wallWidth: {
        input: App.dom.wallWidth,
        error: App.dom.wallWidthError
      },
      ceilingHeight: {
        input: App.dom.ceilingHeight,
        error: App.dom.ceilingHeightError
      },
      furnitureWidth: {
        input: App.dom.furnitureWidth,
        error: App.dom.furnitureWidthError
      },
      furnitureHeight: {
        input: App.dom.furnitureHeight,
        error: App.dom.furnitureHeightError
      }
    };

    return fieldMap[fieldKey] || null;
  };

  App.captureOriginalDescribedBy = function () {
    ["wallWidth", "ceilingHeight", "furnitureWidth", "furnitureHeight"].forEach(function (fieldKey) {
      const field = App.getValidationField(fieldKey);
      if (field && field.input) {
        field.input.dataset.originalDescribedby = field.input.getAttribute("aria-describedby") || "";
      }
    });
  };

  App.clearValidationField = function (fieldKey) {
    const field = App.getValidationField(fieldKey);
    if (!field || !field.input) {
      return;
    }

    field.input.removeAttribute("aria-invalid");

    if (field.error) {
      field.error.textContent = "";
    }

    const originalDescribedBy = field.input.dataset.originalDescribedby || "";
    if (originalDescribedBy) {
      field.input.setAttribute("aria-describedby", originalDescribedBy);
    } else {
      field.input.removeAttribute("aria-describedby");
    }
  };

  App.clearValidationStates = function () {
    ["wallWidth", "ceilingHeight", "furnitureWidth", "furnitureHeight"].forEach(function (fieldKey) {
      App.clearValidationField(fieldKey);
    });
  };

  App.applyValidationStates = function (errors) {
    const appliedFields = {};

    App.clearValidationStates();

    errors.forEach(function (error) {
      if (!error.field || appliedFields[error.field]) {
        return;
      }

      const field = App.getValidationField(error.field);
      if (!field || !field.input) {
        return;
      }

      field.input.setAttribute("aria-invalid", "true");

      if (field.error) {
        field.error.textContent = error.message;
      }

      const originalDescribedBy = field.input.dataset.originalDescribedby || "";
      const describedBy = [originalDescribedBy, field.error && field.error.id].filter(Boolean).join(" ");
      if (describedBy) {
        field.input.setAttribute("aria-describedby", describedBy);
      }

      appliedFields[error.field] = true;
    });
  };

  App.setFurniture = function (value) {
    App.state.hasFurniture = value;
    App.dom.btnFurnitureYes.classList.toggle("active", value);
    App.dom.btnFurnitureNo.classList.toggle("active", !value);
    App.setPressedState(App.dom.btnFurnitureYes, value);
    App.setPressedState(App.dom.btnFurnitureNo, !value);
    App.dom.furnitureSection.classList.toggle("visible", value);
    if (!value) {
      App.clearValidationField("furnitureWidth");
      App.clearValidationField("furnitureHeight");
    }
  };

  App.setRegion = function (value) {
    App.state.region = value;
    App.dom.btnMetric.classList.toggle("active", value === "metric");
    App.dom.btnImperial.classList.toggle("active", value === "imperial");
    App.setPressedState(App.dom.btnMetric, value === "metric");
    App.setPressedState(App.dom.btnImperial, value === "imperial");
    App.refreshDisplayedResult();
  };

  App.setHeadboard = function (value) {
    App.state.hasHeadboard = value;
    App.dom.btnHeadboardYes.classList.toggle("active", value);
    App.dom.btnHeadboardNo.classList.toggle("active", !value);
    App.setPressedState(App.dom.btnHeadboardYes, value);
    App.setPressedState(App.dom.btnHeadboardNo, !value);
    App.applyFurnitureFallback(true);
    App.updateFurnitureDimensionsNote();
  };

  App.isBedFurnitureType = function (furnitureType) {
    const engineKey = App.constants.uiToEngineFurnitureType[furnitureType] || "";
    return engineKey.indexOf("bed_") === 0;
  };

  App.getRoomPreset = function () {
    const roomType = App.dom.roomType.value;
    return App.data.roomPresets[roomType] || App.data.roomPresets.living;
  };

  App.getFurnitureDefaults = function (furnitureType) {
    const furnitureData = App.constants.furnitureDb[furnitureType] || {};
    const preset = App.getRoomPreset();
    const fallbackFurniture = furnitureData.fallbackType
      ? (App.constants.furnitureDb[furnitureData.fallbackType] || {})
      : {};

    let width = furnitureData.w;
    if (width == null && fallbackFurniture.w != null) {
      width = fallbackFurniture.w;
    }
    if ((width == null || furnitureData.usePresetWidth) && preset) {
      width = preset.furnitureWidthCm;
    }

    let height = App.isBedFurnitureType(furnitureType) && App.state.hasHeadboard === false
      ? furnitureData.noHeadboardH
      : furnitureData.h;
    if (height == null && fallbackFurniture.h != null) {
      height = fallbackFurniture.h;
    }
    if (height == null && preset) {
      height = preset.furnitureHeightCm;
    }

    return {
      width: width,
      height: height,
      label: furnitureData.label || "furniture"
    };
  };

  App.populateFurnitureOptions = function () {
    const roomType = App.dom.roomType.value;
    const optionKeys = App.constants.roomFurnitureOptions[roomType] || [];

    App.dom.furnitureType.innerHTML = optionKeys.map(function (optionKey) {
      const option = App.constants.furnitureDb[optionKey];
      return "<option value='" + optionKey + "'>" + option.optionLabel + "</option>";
    }).join("");
  };

  App.applyFurnitureFallback = function (force) {
    const furnitureType = App.dom.furnitureType.value;
    const defaults = App.getFurnitureDefaults(furnitureType);

    if (force || !App.dom.furnitureWidth.value) {
      App.dom.furnitureWidth.value = defaults.width || "";
    }

    if (force || !App.dom.furnitureHeight.value) {
      App.dom.furnitureHeight.value = defaults.height || "";
    }

    if (!force) {
      App.liveRecalc();
    }
  };

  App.updateFurnitureDimensionsVisibility = function () {
    const furnitureType = App.dom.furnitureType.value;
    const shouldShow = App.state.hasFurniture &&
      App.constants.furnitureTypesWithEditableDimensions.indexOf(furnitureType) !== -1;

    App.dom.furnitureDimensionsGroup.classList.toggle("visible", shouldShow);
    if (!shouldShow) {
      App.clearValidationField("furnitureWidth");
      App.clearValidationField("furnitureHeight");
    }
  };

  App.updateFurnitureHeightMin = function () {
    const isBathroomFurniture = App.state.hasFurniture && App.dom.roomType.value === "bathroom";
    const isSofaFurniture = App.state.hasFurniture && App.isSofaFurnitureType(App.dom.furnitureType.value);
    let minHeight = App.constants.inputLimits.furnitureHeight.min;

    if (isSofaFurniture) {
      minHeight = 60;
    }

    if (isBathroomFurniture && App.dom.furnitureType.value === "vanity") {
      minHeight = 70;
    }

    if (isBathroomFurniture && App.dom.furnitureType.value === "bath") {
      minHeight = 40;
    }

    App.dom.furnitureHeight.setAttribute("min", String(minHeight));
  };

  App.updateFurnitureDimensionsNote = function () {
    const furnitureType = App.dom.furnitureType.value;
    const defaults = App.getFurnitureDefaults(App.dom.furnitureType.value);
    const isEditable = App.constants.furnitureTypesWithEditableDimensions.indexOf(furnitureType) !== -1;
    const customEditLabels = {
      armchair: "Custom sofa size",
      sofa2: "Custom sofa size",
      sofa3: "Custom sofa size",
      bedSingle: "Custom bed size",
      bedDouble: "Custom bed size",
      bedKing: "Custom bed size",
      bedSuperKing: "Custom bed size"
    };

    if (!App.dom.furnitureDimensionsNote) {
      return;
    }

    App.dom.furnitureDimensionsNote.style.display = App.state.hasFurniture ? "" : "none";

    if (defaults.width && defaults.height) {
      if (customEditLabels[furnitureType]) {
        App.dom.furnitureDimensionsNote.textContent = "Preset size used: " + defaults.width + " × " + defaults.height + "cm. Choose " + customEditLabels[furnitureType] + " to edit.";
        return;
      }

      if (furnitureType === "customSofa" || furnitureType === "customBed") {
        App.dom.furnitureDimensionsNote.textContent = "Custom size — enter your own measurements.";
        return;
      }

      if (furnitureType === "worktop") {
        App.dom.furnitureDimensionsNote.textContent = "Starting size: " + defaults.width + " × " + defaults.height + "cm — edit below to match your worktop or unit.";
        return;
      }

      if (isEditable) {
        App.dom.furnitureDimensionsNote.textContent = "Starting size: " + defaults.width + " × " + defaults.height + "cm — edit below to match your furniture.";
        return;
      }

      App.dom.furnitureDimensionsNote.textContent = "Preset size used: " + defaults.width + " × " + defaults.height + "cm.";
      return;
    }

    App.dom.furnitureDimensionsNote.textContent = "Auto-filled starting points — edit if your furniture is different.";
  };

  App.isSofaFurnitureType = function (furnitureType) {
    const engineKey = App.constants.uiToEngineFurnitureType[furnitureType] || "";
    return engineKey === "armchair" || engineKey.indexOf("sofa_") === 0;
  };

  App.updateHeadboardVisibility = function () {
    const shouldShow = App.state.hasFurniture && App.isBedFurnitureType(App.dom.furnitureType.value);
    App.dom.headboardField.classList.toggle("visible", shouldShow);
  };

  App.applyRoomPreset = function () {
    const preset = App.getRoomPreset();
    App.populateFurnitureOptions();
    App.dom.wallWidth.value = preset.wallWidthCm;
    App.dom.ceilingHeight.value = preset.ceilingHeightCm;
    App.setFurniture(!!preset.hasFurniture);
    App.dom.furnitureType.value = preset.furnitureType;
    App.applyFurnitureFallback(true);
    App.updateFurnitureDimensionsVisibility();
    App.updateFurnitureHeightMin();
    App.updateFurnitureDimensionsNote();
    App.updateHeadboardVisibility();
    App.liveRecalc();
  };

  App.resetCalc = function () {
    App.dom.resultsPanel.style.display = "none";
    App.dom.resultsPanel.classList.remove("visible");
    App.dom.svgPreviewStage.innerHTML = "";
    App.dom.resultsStatus.textContent = "";
    App.clearValidationStates();
    App.state.selectedSizeOption = "recommended";
    App.state.lastResult = null;
    if (App.dom.roomType) {
      App.dom.roomType.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  App.liveRecalc = function () {
    if (App.state.lastResult) {
      App.calculate();
    }
  };

  App.refreshDisplayedResult = function () {
    if (App.state.lastResult) {
      App.renderResults(App.getSelectedDisplayResult());
    }
  };

  App.getAvailableSizeOption = function (engineResult, optionKey) {
    const sizeOptions = engineResult && engineResult.sizeOptions;
    return sizeOptions && sizeOptions[optionKey] ? sizeOptions[optionKey] : null;
  };

  App.getValidatedSizeOptionKey = function (engineResult, optionKey) {
    return App.getAvailableSizeOption(engineResult, optionKey) ? optionKey : "recommended";
  };

  App.buildDisplayResult = function (engineResult, optionKey) {
    if (!engineResult || engineResult.status !== "ok") {
      return engineResult;
    }

    const selectedKey = App.getValidatedSizeOptionKey(engineResult, optionKey || "recommended");
    const selectedOption = App.getAvailableSizeOption(engineResult, selectedKey);

    if (!selectedOption || selectedKey === "recommended") {
      return Object.assign({}, engineResult, {
        selectedSizeOption: "recommended",
        selectedSizeOptionData: selectedOption || null
      });
    }

    return Object.assign({}, engineResult, {
      recommendation: selectedOption.recommendation,
      previewGeometry: selectedOption.previewGeometry,
      selectedSizeOption: selectedKey,
      selectedSizeOptionData: selectedOption
    });
  };

  App.getSelectedDisplayResult = function () {
    return App.buildDisplayResult(App.state.lastResult, App.state.selectedSizeOption);
  };

  App.selectSizeOption = function (optionKey) {
    if (!App.state.lastResult || !App.getAvailableSizeOption(App.state.lastResult, optionKey)) {
      return;
    }

    App.state.selectedSizeOption = optionKey;
    App.renderResults(App.getSelectedDisplayResult());
  };

  App.captureDom = function () {
    App.dom = {
      roomType: document.getElementById("roomType"),
      wallWidth: document.getElementById("wallWidth"),
      ceilingHeight: document.getElementById("ceilingHeight"),
      btnFurnitureYes: document.getElementById("btnFurnitureYes"),
      btnFurnitureNo: document.getElementById("btnFurnitureNo"),
      furnitureSection: document.getElementById("furnitureSection"),
      furnitureType: document.getElementById("furnitureType"),
      headboardField: document.getElementById("headboardField"),
      btnHeadboardYes: document.getElementById("btnHeadboardYes"),
      btnHeadboardNo: document.getElementById("btnHeadboardNo"),
      furnitureDimensionsGroup: document.getElementById("furnitureDimensionsGroup"),
      furnitureWidth: document.getElementById("furnitureWidth"),
      furnitureHeight: document.getElementById("furnitureHeight"),
      wallWidthError: document.getElementById("wallWidthError"),
      ceilingHeightError: document.getElementById("ceilingHeightError"),
      furnitureWidthError: document.getElementById("furnitureWidthError"),
      furnitureHeightError: document.getElementById("furnitureHeightError"),
      furnitureDimensionsNote: document.getElementById("furnitureDimensionsNote"),
      layoutPref: document.getElementById("layoutPref"),
      btnMetric: document.getElementById("btnMetric"),
      btnImperial: document.getElementById("btnImperial"),
      orientationPref: document.getElementById("orientationPref"),
      calculateBtn: document.getElementById("calculateBtn"),
      resultsStatus: document.getElementById("resultsStatus"),
      resultsPanel: document.getElementById("resultsPanel"),
      previewControls: document.querySelector(".preview-controls"),
      roomPreview: document.getElementById("roomPreview"),
      previewNote: document.getElementById("previewNote"),
      svgPreviewStage: document.getElementById("svgPreviewStage"),
      alertsZone: document.getElementById("alertsZone"),
      keyMeasurementsLabel: document.querySelector(".compact-label"),
      metricsGrid: document.getElementById("metricsGrid"),
      rangeBarWrap: document.querySelector(".range-bar-wrap"),
      rangeMin: document.getElementById("rangeMin"),
      rangeMax: document.getElementById("rangeMax"),
      rangeBarFill: document.getElementById("rangeBarFill"),
      rangeBarIdeal: document.getElementById("rangeBarIdeal"),
      recBlock: document.getElementById("recBlock"),
      installationSection: document.querySelector(".installation-section"),
      hangingInstructions: document.getElementById("hangingInstructions"),
      resetCalc: document.getElementById("resetCalc")
    };
    App.captureOriginalDescribedBy();
  };

  App.bindEvents = function () {
    App.dom.roomType.addEventListener("change", App.applyRoomPreset);
    App.dom.wallWidth.addEventListener("change", App.liveRecalc);
    App.dom.ceilingHeight.addEventListener("change", App.liveRecalc);
    App.dom.furnitureType.addEventListener("change", function () {
      App.applyFurnitureFallback(true);
      App.updateFurnitureDimensionsVisibility();
      App.updateFurnitureHeightMin();
      App.updateFurnitureDimensionsNote();
      App.updateHeadboardVisibility();
      App.liveRecalc();
    });
    App.dom.furnitureWidth.addEventListener("change", App.liveRecalc);
    App.dom.furnitureHeight.addEventListener("change", App.liveRecalc);
    App.dom.layoutPref.addEventListener("change", App.liveRecalc);
    App.dom.orientationPref.addEventListener("change", App.liveRecalc);

    App.dom.btnFurnitureYes.addEventListener("click", function () {
      App.setFurniture(true);
      App.updateFurnitureDimensionsVisibility();
      App.updateFurnitureHeightMin();
      App.updateFurnitureDimensionsNote();
      App.updateHeadboardVisibility();
      App.liveRecalc();
    });

    App.dom.btnFurnitureNo.addEventListener("click", function () {
      App.setFurniture(false);
      App.updateFurnitureDimensionsVisibility();
      App.updateFurnitureHeightMin();
      App.updateFurnitureDimensionsNote();
      App.updateHeadboardVisibility();
      App.liveRecalc();
    });

    App.dom.btnHeadboardYes.addEventListener("click", function () {
      App.setHeadboard(true);
      App.liveRecalc();
    });

    App.dom.btnHeadboardNo.addEventListener("click", function () {
      App.setHeadboard(false);
      App.liveRecalc();
    });

    App.dom.btnMetric.addEventListener("click", function () {
      App.setRegion("metric");
    });

    App.dom.btnImperial.addEventListener("click", function () {
      App.setRegion("imperial");
    });

    App.dom.calculateBtn.addEventListener("click", function () {
      App.calculate({ scrollToResults: true });
    });
    App.dom.resetCalc.addEventListener("click", App.resetCalc);
  };

  App.validateRoomSizeData = function (roomSizes) {
    const errors = [];
    const roomDataByName = {};
    const requiredRealFields = [
      "wall_width_cm",
      "ceiling_height_cm",
      "furniture_width_cm",
      "furniture_top_height_cm"
    ];

    function isPlainObject(value) {
      return value !== null && typeof value === "object" && !Array.isArray(value);
    }

    function hasValidNumber(value) {
      return typeof value === "number" && Number.isFinite(value) && value > 0;
    }

    if (!Array.isArray(roomSizes)) {
      return {
        valid: false,
        errors: ["Room-size data must be an array."],
        roomDataByName: roomDataByName
      };
    }

    roomSizes.forEach(function (room, index) {
      const location = "Room entry " + (index + 1);

      if (!isPlainObject(room)) {
        errors.push(location + " must be an object.");
        return;
      }

      if (typeof room.room_name !== "string" || !room.room_name.trim()) {
        errors.push(location + " must include room_name.");
        return;
      }

      if (roomDataByName[room.room_name]) {
        errors.push("Duplicate room-size entry for " + room.room_name + ".");
      }

      if (!isPlainObject(room.real)) {
        errors.push(room.room_name + " must include a real measurements object.");
      } else {
        requiredRealFields.forEach(function (fieldName) {
          if (!hasValidNumber(room.real[fieldName])) {
            errors.push(room.room_name + " must include a positive numeric real." + fieldName + ".");
          }
        });
      }

      roomDataByName[room.room_name] = room;
    });

    Object.values(App.constants.roomSourceNames).forEach(function (roomName) {
      if (!roomDataByName[roomName]) {
        errors.push("Missing room-size entry for " + roomName + ".");
      }
    });

    return {
      valid: errors.length === 0,
      errors: errors,
      roomDataByName: roomDataByName
    };
  };

  App.showStartupError = function () {
    if (!App.dom || !App.dom.resultsPanel || !App.dom.recBlock) {
      return;
    }

    App.dom.resultsPanel.style.display = "block";
    App.dom.resultsPanel.classList.add("visible");
    App.dom.recBlock.classList.add("recommendation-error");
    App.dom.recBlock.innerHTML =
      "<h2 class=\"rec-header\">Calculator unavailable</h2>" +
      "<div class=\"rec-title\">Room data could not load</div>" +
      "<div class=\"rec-subtitle\" role=\"alert\">The calculator could not load its room data. Please refresh the page or try again later.</div>";

    if (App.dom.resultsStatus) App.dom.resultsStatus.textContent = "The calculator could not load its room data.";
    if (App.dom.alertsZone) App.dom.alertsZone.innerHTML = "";
    if (App.dom.previewControls) App.dom.previewControls.style.display = "none";
    if (App.dom.roomPreview) App.dom.roomPreview.style.display = "none";
    if (App.dom.keyMeasurementsLabel) App.dom.keyMeasurementsLabel.style.display = "none";
    if (App.dom.metricsGrid) {
      App.dom.metricsGrid.innerHTML = "";
      App.dom.metricsGrid.style.display = "none";
    }
    if (App.dom.rangeBarWrap) App.dom.rangeBarWrap.style.display = "none";
    if (App.dom.installationSection) App.dom.installationSection.style.display = "none";

    const shopCtaWrap = document.getElementById("shopCtaWrap");
    const shopCtaBlock = document.getElementById("shopCtaBlock");
    if (shopCtaBlock) shopCtaBlock.innerHTML = "";
    if (shopCtaWrap) shopCtaWrap.style.display = "none";
  };

  App.loadData = async function () {
    async function fetchRoomSizes(url) {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Could not load calculator room data from " + url + ".");
      }

      return response.json();
    }

    async function resolveRoomSizes() {
      const config = window.WallArtCalculatorConfig || {};

      if (config.roomSizes) {
        return config.roomSizes;
      }

      if (config.roomSizesUrl) {
        return fetchRoomSizes(config.roomSizesUrl);
      }

      return fetchRoomSizes("data/room-sizes.json");
    }

    let roomSizes;
    try {
      roomSizes = await resolveRoomSizes();
    } catch (error) {
      console.error("Wall Art Calculator could not load room-size data.", error);
      throw error;
    }

    const validation = App.validateRoomSizeData(roomSizes);
    if (!validation.valid) {
      const error = new Error("Wall Art Calculator room-size data must match data/room-sizes.json.");
      error.details = validation.errors;
      console.error("Wall Art Calculator: invalid room size data", validation.errors);
      throw error;
    }

    App.data.roomSizes = roomSizes;
    const roomDataByName = validation.roomDataByName;

    App.data.roomPresets = Object.fromEntries(
      Object.entries(App.constants.roomSourceNames).map(function (entry) {
        const roomKey = entry[0];
        const roomName = entry[1];
        const room = roomDataByName[roomName];
        const defaults = App.constants.roomDefaults[roomKey];

        return [roomKey, {
          wallWidthCm: room.real.wall_width_cm,
          ceilingHeightCm: room.real.ceiling_height_cm,
          hasFurniture: defaults.hasFurniture,
          furnitureType: defaults.furnitureType,
          furnitureWidthCm: room.real.furniture_width_cm,
          furnitureHeightCm: room.real.furniture_top_height_cm
        }];
      })
    );

    App.data.engineRoomPresets = Object.fromEntries(
      Object.entries(App.data.roomPresets).map(function (entry) {
        const roomType = entry[0];
        const preset = entry[1];

        return [roomType, {
          defaultWallWidthCm: preset.wallWidthCm,
          defaultCeilingHeightCm: preset.ceilingHeightCm,
          roomFlags: (App.constants.roomFlagsByType[roomType] || []).slice(),
          allowWallCentredFallback: false
        }];
      })
    );
  };

  App.buildEngineConfig = function () {
    return {
      roomPresets: App.data.engineRoomPresets,
      furnitureDefaults: App.constants.engineFurnitureDefaults
    };
  };

  App.mapUiFurnitureToEngineKey = function (furnitureType) {
    return App.constants.uiToEngineFurnitureType[furnitureType] || "none";
  };

  App.buildEngineInput = function () {
    const roomType = App.dom.roomType.value;
    const furnitureType = App.dom.furnitureType.value;
    const hasAnchor = !!App.state.hasFurniture;
    const engineFurnitureTypeKey = hasAnchor
      ? App.mapUiFurnitureToEngineKey(furnitureType)
      : "none";
    const furnitureWidth = parseFloat(App.dom.furnitureWidth.value);
    const furnitureHeight = parseFloat(App.dom.furnitureHeight.value);

    return {
      inputUnit: "cm",
      roomType: roomType,
      wall: {
        width: parseFloat(App.dom.wallWidth.value) || null,
        ceilingHeight: parseFloat(App.dom.ceilingHeight.value) || null,
        usableLeftLimit: null,
        usableRightLimit: null
      },
      anchor: {
        hasAnchor: hasAnchor,
        furnitureTypeKey: engineFurnitureTypeKey,
        width: hasAnchor && !Number.isNaN(furnitureWidth) ? furnitureWidth : null,
        topHeight: hasAnchor && !Number.isNaN(furnitureHeight) ? furnitureHeight : null,
        centreX: null,
        hasHeadboard: engineFurnitureTypeKey.indexOf("bed_") === 0 ? App.state.hasHeadboard : null
      },
      preferences: {
        layout: App.constants.layoutPreferenceMap[App.dom.layoutPref.value] || "auto",
        orientation: App.constants.orientationPreferenceMap[App.dom.orientationPref.value] || "auto"
      }
    };
  };

  App.validationErrorResult = function (errors) {
    const validationErrors = errors.map(function (error) {
      return typeof error === "string"
        ? { message: error, field: null }
        : error;
    });

    return {
      status: "error",
      resolvedInputs: null,
      recommendation: null,
      previewGeometry: null,
      warnings: [],
      assumptionsUsed: [],
      errors: validationErrors.map(function (error) {
        return {
          code: "INPUT_VALIDATION",
          meta: {
            message: error.message,
            field: error.field || null
          }
        };
      })
    };
  };

  App.validateCalculatorInputs = function () {
    const limits = App.constants.inputLimits;
    const errors = [];
    const wallWidth = parseFloat(App.dom.wallWidth.value);
    const ceilingHeight = parseFloat(App.dom.ceilingHeight.value);
    const furnitureWidth = parseFloat(App.dom.furnitureWidth.value);
    const furnitureHeight = parseFloat(App.dom.furnitureHeight.value);
    const isSofaFurniture = App.isSofaFurnitureType(App.dom.furnitureType.value);
    const isBathroomVanity = App.dom.roomType.value === "bathroom" && App.dom.furnitureType.value === "vanity";
    const isBathroomBath = App.dom.roomType.value === "bathroom" && App.dom.furnitureType.value === "bath";
    const requiresFurnitureDimensions = App.state.hasFurniture &&
      App.constants.furnitureTypesWithEditableDimensions.indexOf(App.dom.furnitureType.value) !== -1;
    const wallWidthInRange = hasNumber(wallWidth) && wallWidth >= limits.wallWidth.min && wallWidth <= limits.wallWidth.max;
    const ceilingHeightInRange = hasNumber(ceilingHeight) && ceilingHeight >= limits.ceilingHeight.min && ceilingHeight <= limits.ceilingHeight.max;
    const furnitureWidthInRange = hasNumber(furnitureWidth) && furnitureWidth >= limits.furnitureWidth.min && furnitureWidth <= limits.furnitureWidth.max;
    const furnitureHeightInRange = hasNumber(furnitureHeight) && furnitureHeight >= limits.furnitureHeight.min && furnitureHeight <= limits.furnitureHeight.max;

    function hasNumber(value) {
      return !Number.isNaN(value);
    }

    function addError(field, message) {
      errors.push({
        field: field,
        message: message
      });
    }

    if (!hasNumber(wallWidth)) {
      addError("wallWidth", "Enter a wall width.");
    } else if (wallWidth < limits.wallWidth.min || wallWidth > limits.wallWidth.max) {
      addError("wallWidth", "Wall width must be between 80cm and 2000cm.");
    }

    if (!hasNumber(ceilingHeight)) {
      addError("ceilingHeight", "Enter a ceiling height.");
    } else if (ceilingHeight < limits.ceilingHeight.min || ceilingHeight > limits.ceilingHeight.max) {
      addError("ceilingHeight", "Ceiling height must be between 180cm and 600cm.");
    }

    if (App.state.hasFurniture) {
      if (requiresFurnitureDimensions && !hasNumber(furnitureWidth)) {
        addError("furnitureWidth", "Enter a furniture width.");
      } else if (hasNumber(furnitureWidth) && (furnitureWidth < limits.furnitureWidth.min || furnitureWidth > limits.furnitureWidth.max)) {
        addError("furnitureWidth", "Furniture width must be between 40cm and 1200cm.");
      }

      if (wallWidthInRange && furnitureWidthInRange && furnitureWidth > wallWidth) {
        addError("furnitureWidth", "Furniture width cannot be wider than the wall.");
      }

      if (requiresFurnitureDimensions && !hasNumber(furnitureHeight)) {
        addError("furnitureHeight", "Enter a furniture height.");
      } else if (hasNumber(furnitureHeight) && (furnitureHeight < limits.furnitureHeight.min || furnitureHeight > limits.furnitureHeight.max)) {
        addError("furnitureHeight", "Furniture height must be between 20cm and 350cm.");
      }

      if (isSofaFurniture && furnitureHeightInRange && furnitureHeight < 60) {
        addError("furnitureHeight", "Sofa height must be at least 60cm.");
      }

      if (isBathroomVanity && furnitureHeightInRange && furnitureHeight < 70) {
        addError("furnitureHeight", "Vanity height must be at least 70cm.");
      }

      if (isBathroomBath && furnitureHeightInRange && furnitureHeight < 40) {
        addError("furnitureHeight", "Bath height must be at least 40cm.");
      }

      if (
        ceilingHeightInRange &&
        furnitureHeightInRange &&
        furnitureHeight > ceilingHeight - limits.furnitureCeilingClearanceMin
      ) {
        addError("furnitureHeight", "Furniture height must be at least 20cm below the ceiling.");
      }
    }

    return {
      valid: errors.length === 0,
      messages: errors.map(function (error) {
        return error.message;
      }),
      errors: errors
    };
  };

  App.getPreviewFallbackMessage = function (engineResult) {
    if (!engineResult || engineResult.status !== "ok") {
      return null;
    }

    const limits = App.constants.inputLimits;
    const inputs = engineResult.resolvedInputs || {};

    if (
      inputs.wallWidthCm > limits.wallWidth.previewMax ||
      inputs.ceilingHeightCm > limits.ceilingHeight.previewMax
    ) {
      return "Preview unavailable for these dimensions. Use the measurements below.";
    }

    if (
      inputs.anchorType !== "none" &&
      (
        inputs.anchorWidthCm > limits.furnitureWidth.previewMax ||
        inputs.anchorTopHeightCm > limits.furnitureHeight.previewMax ||
        inputs.anchorTopHeightCm > inputs.ceilingHeightCm - limits.furnitureCeilingClearancePreviewMin
      )
    ) {
      return "Preview unavailable for these dimensions. Use the measurements below.";
    }

    return null;
  };

  App.calculateWithEngine = function (options) {
    if (typeof createWallArtEngine !== "function") {
      window.alert("The new calculator engine is not available.");
      return;
    }

    if (!App.state.engine) {
      window.alert("The new calculator engine has not been initialised.");
      return;
    }

    const validation = App.validateCalculatorInputs();
    if (!validation.valid) {
      App.applyValidationStates(validation.errors);
      const validationResult = App.validationErrorResult(validation.errors);
      App.state.selectedSizeOption = "recommended";
      App.state.lastResult = null;
      App.renderResults(validationResult, options || {});
      return;
    }

    App.clearValidationStates();

    const engineInput = App.buildEngineInput();
    const engineResult = App.state.engine(engineInput);
    const previewFallbackMessage = App.getPreviewFallbackMessage(engineResult);
    const displayEngineResult = engineResult.status === "ok"
      ? Object.assign({}, engineResult, { previewFallbackMessage: previewFallbackMessage })
      : engineResult;

    App.state.selectedSizeOption = "recommended";
    App.state.lastResult = displayEngineResult.status === "ok"
      ? displayEngineResult
      : null;
    App.renderResults(displayEngineResult.status === "ok" ? App.getSelectedDisplayResult() : displayEngineResult, options || {});
  };

  App.start = async function () {
    App.captureDom();

    try {
      await App.loadData();
      App.state.engine = createWallArtEngine(App.buildEngineConfig());
    } catch (error) {
      console.error(error);
      App.showStartupError();
      return;
    }

    App.calculate = App.calculateWithEngine;
    App.bindEvents();
    App.applyRoomPreset();
  };

  document.addEventListener("DOMContentLoaded", App.start);
}());
